import { db } from '../firebase'
import {
  collection,
  addDoc,
  updateDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore'

// Invoice data shape stored in Firestore
export interface InvoiceData {
  studentId: string
  studentName: string
  amount: number
  date: string
  status: 'Paid' | 'Pending'
  billingType?: 'Monthly' | 'Per Lesson'
  billingPeriod?: string
  lessonDate?: string
}

// Invoice as returned from Firestore (with id + timestamps)
export interface Invoice extends InvoiceData {
  id: string
  createdAt?: any
  updatedAt?: any
}

// Helper: remove undefined values before writing to Firestore
const removeUndefinedValues = (
  obj: Record<string, any>,
): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined),
  )
}

/**
 * Fetch all invoices for a teacher, ordered by date descending.
 */
export async function getTeacherInvoices(
  teacherId: string,
): Promise<Invoice[]> {
  const invoicesRef = collection(db, 'teachers', teacherId, 'invoices')
  const snapshot = await getDocs(invoicesRef)

  const invoices = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as InvoiceData),
    createdAt: doc.data().createdAt,
    updatedAt: doc.data().updatedAt,
  }))

  // Sort client-side by date descending (avoids needing a Firestore index)
  return invoices.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
}

/**
 * Create a new invoice in Firestore.
 */
export async function createInvoice(
  teacherId: string,
  invoiceData: InvoiceData,
): Promise<string> {
  const invoicesRef = collection(db, 'teachers', teacherId, 'invoices')

  const dataToWrite = removeUndefinedValues({
    ...invoiceData,
    amount: Number(invoiceData.amount),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })

  const docRef = await addDoc(invoicesRef, dataToWrite)
  return docRef.id
}

/**
 * Update an existing invoice in Firestore.
 */
export async function updateInvoice(
  teacherId: string,
  invoiceId: string,
  updates: Partial<InvoiceData>,
): Promise<void> {
  const invoiceRef = doc(db, 'teachers', teacherId, 'invoices', invoiceId)

  const dataToWrite = removeUndefinedValues({
    ...updates,
    ...(updates.amount !== undefined ? { amount: Number(updates.amount) } : {}),
    updatedAt: serverTimestamp(),
  })

  await updateDoc(invoiceRef, dataToWrite)
}

/**
 * Delete an invoice from Firestore.
 */
export async function deleteInvoice(
  teacherId: string,
  invoiceId: string,
): Promise<void> {
  const invoiceRef = doc(db, 'teachers', teacherId, 'invoices', invoiceId)
  await deleteDoc(invoiceRef)
}
